import React, { useEffect, useCallback, useState } from 'react';
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Paper,
    Button,
    TextField,
} from '@mui/material';
import StorageService from '../../../features/storageManagement/services/StorageService.ts';
import { getDrugDisplayName } from '../../../common/utils/getDrugDisplayName.ts';
import { useGlobalStore } from '../../../application/store/store';
import { TransactionOverview } from '../../../application/types/transactionType.ts';

interface DrugsInStorageProps {
    storageId: string;
}

const DrugsInStorage: React.FC<DrugsInStorageProps> = ({ storageId }) => {
    const { selectedEmergency } = useGlobalStore();
    const drugsInStorage =
        useGlobalStore((state) => state.drugsInStorage) || [];
    const setDrugsInStorage = useGlobalStore(
        (state) => state.setDrugsInStorage
    );
    const shiftChangeInventoryOverview = useGlobalStore(
        (state) => state.shiftChangeInventoryOverview
    );

    const setShouldBeInventory = useGlobalStore(
        (state) => state.setShouldBeInventory
    );

    const { drugQuantities, setDrugQuantities } = useGlobalStore(
        (state) => state.drugQuantitiesView
    );
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const editMode = useGlobalStore((state) => state.editMode);
    const setEditMode = useGlobalStore((state) => state.setEditMode);

    const storageService = new StorageService();

    // sets the drugs in the table
    const fetchDrugsInStorage = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        try {
            const response =
                await storageService.getStorageInventory(storageId);
            setShouldBeInventory({
                storageLocation: selectedEmergency,
                inventory: response.data.inventory,
            });
            const inventoryDrugs =
                response.data.inventory.map((inventory) => ({
                    drug: {
                        id: inventory.drug.id,
                        drugName: inventory.drug.drugName,
                        concentration: inventory.drug.concentration,
                        concentrationUnit: inventory.drug.concentrationUnit,
                        quantity: inventory.drug.quantity,
                        quantityUnit: inventory.drug.quantityUnit,
                        dosageForm: inventory.drug.dosageForm,
                        atc: inventory.drug.atc,
                        isActive: inventory.drug.isActive,
                    },
                    quantityRemaining: inventory.quantityRemaining,
                    saldoThresholds: inventory.saldoThresholds,
                })) || [];
            setDrugsInStorage(inventoryDrugs);
            const formValues =
                response.data.inventory.map((inventory) => ({
                    drugId: inventory.drug.id,
                    drugName: getDrugDisplayName(inventory.drug),
                    // Default to max values because emergency bags should always be fully stocked.
                    quantityAccordingToUser: inventory.saldoThresholds.maxValue,
                })) || [];
            setDrugQuantities(formValues);
            setDrugQuantities(formValues);
        } catch (err) {
            console.error('Error fetching drugs in storage:', err);
            setError('Failed to fetch inventory for the selected storage.');
        } finally {
            setIsLoading(false);
        }
    }, [storageId, setDrugsInStorage]);

    useEffect(() => {
        fetchDrugsInStorage();
    }, [fetchDrugsInStorage]);

    const handleEditClick = () => {
        setEditMode(true);
    };

    const handleSaveClick = () => {
        const updatedDrugs = drugsInStorage.map((drug) => ({
            ...drug,
            quantityRemaining: drugQuantities.find(
                (d) => d.drugId === drug.drug.id
            ).quantityAccordingToUser,
        }));
        setDrugsInStorage(updatedDrugs);
        setEditMode(false);
    };

    const handleChange = (drugId: string, value: number) => {
        drugQuantities.forEach((drug) => {
            if (drug.drugId === drugId) {
                drug.quantityAccordingToUser = value;
            }
        });
        setDrugQuantities([...drugQuantities]);
    };

    return (
        <>
            {error && (
                <Typography color="error" variant="body1" mb={2}>
                    {error}
                </Typography>
            )}
            {isLoading ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Läkemedel</TableCell>
                                    <TableCell>Antal</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {Array.isArray(drugQuantities) &&
                                    drugQuantities.map((drug) => (
                                        <TableRow
                                            key={drug.drugId}
                                            className="odd:bg-gray-100"
                                        >
                                            <TableCell>
                                                {drug.drugName}
                                            </TableCell>
                                            <TableCell>
                                                {editMode ? (
                                                    <TextField
                                                        value={
                                                            drug.quantityAccordingToUser
                                                        }
                                                        onChange={(e) =>
                                                            handleChange(
                                                                drug.drugId,
                                                                Number(
                                                                    e.target
                                                                        .value
                                                                )
                                                            )
                                                        }
                                                        type="number"
                                                        sx={{ width: '80px' }}
                                                        disabled={
                                                            shiftChangeInventoryOverview ===
                                                            TransactionOverview.doubleSignOverview
                                                        }
                                                    />
                                                ) : (
                                                    drug.quantityAccordingToUser
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            width: '100%',
                            marginTop: 2,
                        }}
                    >
                        <Button
                            disabled={
                                shiftChangeInventoryOverview ===
                                TransactionOverview.doubleSignOverview
                            }
                            onClick={
                                editMode ? handleSaveClick : handleEditClick
                            }
                            sx={{ marginTop: 2 }}
                        >
                            {editMode ? 'Spara' : 'Ändra'}
                        </Button>
                    </Box>
                </>
            )}
        </>
    );
};

export default DrugsInStorage;
